import { Input } from '@shared/components/ui';
import { Calendar, Clock, PlayCircle, Lock } from 'lucide-react';
import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePlanLimits } from '@features/subscription/hooks/usePlanLimits';
import { calculateCronInterval, validateCronInterval } from '@features/backups/utils/cronInterval';

interface ScheduleProps {
  data: {
    schedule_type: 'DAILY' | 'WEEKLY' | 'MONTHLY' | 'MINUTELY' | 'CUSTOM';
    time?: string;
    day_of_week?: number;
    day_of_month?: number;
    interval_minutes?: number;
    cron_expression?: string;
    timezone?: string;
    run_missing_jobs_on_startup?: boolean;
  };
  onChange: (data: unknown) => void;
}

// Common timezone options
const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC (UTC+0)' },
  { value: 'America/New_York', label: 'Eastern Time (ET) (UTC-5)' },
  { value: 'America/Chicago', label: 'Central Time (CT) (UTC-6)' },
  { value: 'America/Denver', label: 'Mountain Time (MT) (UTC-7)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT) (UTC-8)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (BRT) (UTC-3)' },
  { value: 'Europe/London', label: 'London (GMT) (UTC+0)' },
  { value: 'Europe/Paris', label: 'Paris (CET) (UTC+1)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET) (UTC+1)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST) (UTC+9)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST) (UTC+8)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT) (UTC+8)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT) (UTC+11)' },
  { value: 'Pacific/Auckland', label: 'Auckland (NZDT) (UTC+13)' },
];

// Get browser's local timezone
const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
};

export function Schedule({ data, onChange }: ScheduleProps) {
  const { t } = useTranslation('backups');
  const navigate = useNavigate();
  const { planLimits, isLoading } = usePlanLimits();
  const [errors, setErrors] = useState<{
    schedule_type?: string;
    time?: string;
    day_of_week?: string;
    day_of_month?: string;
    interval_minutes?: string;
    cron_expression?: string;
  }>({});

  const minScheduleInterval = planLimits?.minScheduleInterval ?? 60;

  // CRON validation for CUSTOM type
  const cronValidation = useMemo(() => {
    if (data.schedule_type !== 'CUSTOM' || !data.cron_expression) {
      return { isValid: true, error: null, intervalMinutes: null };
    }

    const result = calculateCronInterval(data.cron_expression);
    if (!result.isValid) {
      return { isValid: false, error: result.error, intervalMinutes: null };
    }

    const validation = validateCronInterval(data.cron_expression, minScheduleInterval);
    return {
      isValid: validation.isValid,
      error: validation.error,
      intervalMinutes: result.minutes,
    };
  }, [data.cron_expression, data.schedule_type, minScheduleInterval]);

  // Filter interval options based on plan limits
  const intervalOptions = useMemo(() => {
    const options = [
      { value: 5, label: t('wizard.schedule.interval.every5min'), minutes: 5 },
      { value: 10, label: t('wizard.schedule.interval.every10min'), minutes: 10 },
      { value: 15, label: t('wizard.schedule.interval.every15min'), minutes: 15 },
      { value: 20, label: t('wizard.schedule.interval.every20min'), minutes: 20 },
      { value: 30, label: t('wizard.schedule.interval.every30min'), minutes: 30 },
      { value: 60, label: t('wizard.schedule.interval.every60min'), minutes: 60 },
    ];
    return options.map((option) => ({
      ...option,
      isDisabled: option.minutes < minScheduleInterval,
    }));
  }, [minScheduleInterval, t]);

  const handleIntervalChange = (value: string) => {
    const minutes = parseInt(value, 10);
    const option = intervalOptions.find((o) => o.value === minutes);

    // Only allow selection if not disabled
    if (option && !option.isDisabled) {
      onChange({ ...data, interval_minutes: minutes });
      setErrors({ ...errors, interval_minutes: undefined });
    }
  };

  const handleUpgradeClick = () => {
    navigate('/billing');
  };

  const daysOfWeek = [
    { value: 0, label: t('wizard.schedule.days.0') },
    { value: 1, label: t('wizard.schedule.days.1') },
    { value: 2, label: t('wizard.schedule.days.2') },
    { value: 3, label: t('wizard.schedule.days.3') },
    { value: 4, label: t('wizard.schedule.days.4') },
    { value: 5, label: t('wizard.schedule.days.5') },
    { value: 6, label: t('wizard.schedule.days.6') },
  ];

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-3 ml-1">
          {t('wizard.schedule.scheduleType')}
        </label>
        <div className="grid grid-cols-2 gap-3">
          {[
            { value: 'DAILY', label: t('wizard.schedule.frequency.daily'), icon: Clock },
            { value: 'WEEKLY', label: t('wizard.schedule.frequency.weekly'), icon: Calendar },
            { value: 'MONTHLY', label: t('wizard.schedule.frequency.monthly'), icon: Calendar },
            { value: 'MINUTELY', label: t('wizard.schedule.everyXMinutes'), icon: Clock },
            { value: 'CUSTOM', label: t('wizard.schedule.frequency.custom'), icon: Clock },
          ].map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange({
                    ...data,
                    schedule_type: option.value as
                      'DAILY' | 'WEEKLY' | 'MONTHLY' | 'MINUTELY' | 'CUSTOM',
                  });
                  setErrors({ ...errors, schedule_type: undefined });
                }}
                className={`p-4 rounded-xl border-2 transition-all text-left ${
                  data.schedule_type === option.value
                    ? 'border-brand-600 bg-accent-soft-light dark:bg-accent-soft-dark'
                    : 'border-surface-border-light dark:border-surface-border-dark hover:border-border-strong-light dark:hover:border-border-strong-dark'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <Icon
                    className={
                      data.schedule_type === option.value
                        ? 'text-brand-600 dark:text-brand-400'
                        : 'text-text-subtle-light dark:text-text-subtle-dark'
                    }
                    size={20}
                    strokeWidth={2.5}
                  />
                  <span className="font-black text-sm text-slate-900 dark:text-white">
                    {option.label}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
        {errors.schedule_type && (
          <p className="mt-2 text-xs font-bold text-state-danger-light dark:text-state-danger-dark ml-1">
            {errors.schedule_type}
          </p>
        )}
      </div>

      {data.schedule_type === 'DAILY' && (
        <Input
          label={t('wizard.schedule.time')}
          type="time"
          value={data.time || ''}
          onChange={(e) => {
            onChange({ ...data, time: e.target.value });
            setErrors({ ...errors, time: undefined });
          }}
          error={errors.time}
        />
      )}

      {data.schedule_type === 'WEEKLY' && (
        <div className="space-y-4">
          <Input
            label={t('wizard.schedule.time')}
            type="time"
            value={data.time || ''}
            onChange={(e) => {
              onChange({ ...data, time: e.target.value });
              setErrors({ ...errors, time: undefined });
            }}
            error={errors.time}
          />
          <div>
            <label
              htmlFor="backup-schedule-day-of-week"
              className="block text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-3 ml-1"
            >
              {t('wizard.schedule.dayOfWeek')}
            </label>
            <select
              id="backup-schedule-day-of-week"
              value={data.day_of_week ?? ''}
              onChange={(e) => {
                onChange({ ...data, day_of_week: parseInt(e.target.value) || undefined });
                setErrors({ ...errors, day_of_week: undefined });
              }}
              className="w-full px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
            >
              <option value="">{t('wizard.schedule.selectDay')}</option>
              {daysOfWeek.map((day) => (
                <option key={day.value} value={day.value}>
                  {day.label}
                </option>
              ))}
            </select>
            {errors.day_of_week && (
              <p className="mt-2 text-xs font-bold text-state-danger-light dark:text-state-danger-dark ml-1">
                {errors.day_of_week}
              </p>
            )}
          </div>
        </div>
      )}

      {data.schedule_type === 'MONTHLY' && (
        <div className="space-y-4">
          <Input
            label={t('wizard.schedule.time')}
            type="time"
            value={data.time || ''}
            onChange={(e) => {
              onChange({ ...data, time: e.target.value });
              setErrors({ ...errors, time: undefined });
            }}
            error={errors.time}
          />
          <div>
            <label
              htmlFor="backup-schedule-day-of-month"
              className="block text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-3 ml-1"
            >
              {t('wizard.schedule.dayOfMonth')}
            </label>
            <select
              id="backup-schedule-day-of-month"
              value={data.day_of_month ?? ''}
              onChange={(e) => {
                onChange({ ...data, day_of_month: parseInt(e.target.value) || undefined });
                setErrors({ ...errors, day_of_month: undefined });
              }}
              className="w-full px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
            >
              <option value="">{t('wizard.schedule.selectDay')}</option>
              {Array.from({ length: 28 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {t('wizard.schedule.dayPrefix')} {i + 1}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {data.schedule_type === 'MINUTELY' && (
        <div>
          <label
            htmlFor="backup-schedule-interval"
            className="block text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-3 ml-1"
          >
            {t('wizard.schedule.intervalMinutes')}
          </label>
          <div className="relative">
            <select
              id="backup-schedule-interval"
              value={data.interval_minutes || ''}
              onChange={(e) => handleIntervalChange(e.target.value)}
              disabled={isLoading}
              className={`
                w-full px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark
                border border-border-strong-light dark:border-border-strong-dark
                rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none
                appearance-none cursor-pointer
                ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
                ${errors.interval_minutes ? 'border-state-danger-light dark:border-state-danger-dark focus:ring-state-danger-light dark:focus:ring-state-danger-dark' : ''}
              `}
            >
              <option value="">{t('wizard.schedule.selectInterval')}</option>
              {intervalOptions.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.isDisabled}
                  className={
                    option.isDisabled
                      ? 'text-text-subtle-light dark:text-text-subtle-dark bg-surface-2-light dark:bg-surface-2-dark'
                      : ''
                  }
                >
                  {option.label}
                  {option.isDisabled ? ' ⬆ Upgrade' : ''}
                </option>
              ))}
            </select>
            <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
              <svg
                className="w-5 h-5 text-text-subtle-light dark:text-text-subtle-dark"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>

          {/* Upgrade notice for disabled selection */}
          {data.interval_minutes && data.interval_minutes < minScheduleInterval && (
            <div className="mt-2 p-2 bg-state-warn-bg-light dark:bg-state-warn-bg-dark border border-state-warn-light/20 dark:border-state-warn-dark/20 rounded-lg">
              <div className="flex items-start space-x-2">
                <Lock
                  size={14}
                  className="mt-0.5 shrink-0 text-state-warn-light dark:text-state-warn-dark"
                />
                <div className="text-xs">
                  <p className="font-bold text-state-warn-light dark:text-state-warn-dark">
                    {t('wizard.schedule.upgradeRequired')}
                  </p>
                  <p className="text-state-warn-light dark:text-state-warn-dark mt-0.5">
                    {t('wizard.schedule.upgradeRequiredDesc', { min: minScheduleInterval })}
                  </p>
                  <button
                    onClick={handleUpgradeClick}
                    className="mt-1 text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 font-bold underline"
                  >
                    {t('wizard.schedule.upgradePlan')}
                  </button>
                </div>
              </div>
            </div>
          )}

          {errors.interval_minutes && !isLoading && (
            <p className="mt-2 text-xs font-bold text-state-danger-light dark:text-state-danger-dark ml-1">
              {errors.interval_minutes}
            </p>
          )}

          {/* Plan limit info */}
          {planLimits && !isLoading && (
            <div className="mt-2 flex items-center text-[10px] text-text-subtle-light dark:text-text-subtle-dark font-medium">
              <Clock size={12} className="mr-1" />
              {t('wizard.schedule.minIntervalInfo', { min: minScheduleInterval })}
            </div>
          )}
        </div>
      )}

      {data.schedule_type === 'CUSTOM' && (
        <div>
          <Input
            label={t('wizard.schedule.cronExpression')}
            placeholder={t('wizard.schedule.cronPlaceholder')}
            value={data.cron_expression || ''}
            onChange={(e) => {
              onChange({ ...data, cron_expression: e.target.value });
              setErrors({ ...errors, cron_expression: undefined });
            }}
            error={errors.cron_expression}
          />
          {cronValidation.isValid && cronValidation.intervalMinutes && (
            <p className="text-xs text-brand-600 dark:text-brand-400 mt-2 font-medium bg-accent-soft-light dark:bg-accent-soft-dark p-2 rounded-lg">
              {t('wizard.schedule.intervalLabel')}: {cronValidation.intervalMinutes}{' '}
              {t('wizard.alerts.minutes')}
              {cronValidation.intervalMinutes >= minScheduleInterval && (
                <span className="text-state-ok-light dark:text-state-ok-dark ml-2">
                  {t('wizard.schedule.validForPlan')}
                </span>
              )}
            </p>
          )}
        </div>
      )}

      {/* Timezone Selector */}
      <div className="pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
        <label
          htmlFor="backup-schedule-timezone"
          className="block text-[10px] font-black uppercase tracking-widest text-text-muted-light dark:text-text-muted-dark mb-3 ml-1"
        >
          {t('wizard.schedule.timezone')}
        </label>
        <select
          id="backup-schedule-timezone"
          value={data.timezone || getBrowserTimezone()}
          onChange={(e) => onChange({ ...data, timezone: e.target.value })}
          className="w-full px-4 py-3 bg-surface-card-light dark:bg-surface-card-dark border border-surface-border-light dark:border-surface-border-dark rounded-xl text-sm font-bold focus:ring-2 focus:ring-brand-500 outline-none"
        >
          {TIMEZONE_OPTIONS.map((tz) => (
            <option key={tz.value} value={tz.value}>
              {tz.label}
            </option>
          ))}
        </select>
        <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-2 ml-1">
          {t('wizard.schedule.timezoneInfo')}{' '}
          <span className="font-bold text-slate-700 dark:text-slate-300">
            {data.timezone || getBrowserTimezone()}
          </span>
        </p>
      </div>

      {/* Run Missing Jobs on Startup Option */}
      <div className="pt-4 border-t border-surface-border-light dark:border-surface-border-dark">
        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={data.run_missing_jobs_on_startup || false}
            onChange={(e) => onChange({ ...data, run_missing_jobs_on_startup: e.target.checked })}
            className="mt-1 w-4 h-4 text-brand-600 dark:text-brand-400 border-border-strong-light dark:border-border-strong-dark rounded focus:ring-brand-500"
          />
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <PlayCircle size={16} className="text-brand-600 dark:text-brand-400" />
              <span className="font-black text-sm text-slate-900 dark:text-white">
                {t('wizard.schedule.runMissing')}
              </span>
            </div>
            <p className="text-xs text-text-muted-light dark:text-text-muted-dark mt-1">
              {t('wizard.schedule.runMissingDesc')}
            </p>
          </div>
        </label>
      </div>
    </div>
  );
}

export default Schedule;
